import { Fragment, memo, type ReactNode } from "react";
import {
  AssignmentLateOutlined,
  FactCheckOutlined,
  CheckCircleOutline,
  HighlightOffOutlined,
  HourglassEmptyOutlined,
  HowToRegOutlined,
  PeopleOutline,
  PersonAddAltOutlined,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import {
  isDocDependentFilter,
  PATIENT_OP_FILTER_GROUPS,
  PATIENT_OP_FILTER_OPTIONS,
  type EcommerceListAudience,
  type PatientOpFilter,
} from "src-next/modules/ecommerce";
import { EcommerceQuickStatCard } from "./EcommerceQuickStatCard";

interface EcommercePatientQuickStatsProps {
  totalCount: number;
  docsPendingCount: number;
  docsRejectedCount: number;
  docsToApproveCount: number;
  awaitingReleaseCount: number;
  docsReleasedCount: number;
  purchaseCompletedCount: number;
  activeFilter: PatientOpFilter | null;
  statsReady?: boolean;
  onFilterChange: (filter: PatientOpFilter) => void;
  audience: EcommerceListAudience;
  consentPatientCount: number;
  registeredClientCount: number;
  registeredClientsReady?: boolean;
  onAudienceChange: (audience: EcommerceListAudience) => void;
}

const ICONS: Record<PatientOpFilter, ReactNode> = {
  docs_pending: <AssignmentLateOutlined sx={{ fontSize: 20 }} />,
  docs_rejected: <HighlightOffOutlined sx={{ fontSize: 20 }} />,
  docs_to_approve: <FactCheckOutlined sx={{ fontSize: 20 }} />,
  awaiting_release: <HourglassEmptyOutlined sx={{ fontSize: 20 }} />,
  docs_released: <CheckCircleOutline sx={{ fontSize: 20 }} />,
  purchase_completed: <ShoppingBagOutlined sx={{ fontSize: 20 }} />,
  all: <PeopleOutline sx={{ fontSize: 20 }} />,
};

export default memo(function EcommercePatientQuickStats({
  totalCount,
  docsPendingCount,
  docsRejectedCount,
  docsToApproveCount,
  awaitingReleaseCount,
  docsReleasedCount,
  purchaseCompletedCount,
  activeFilter,
  statsReady = true,
  onFilterChange,
  audience,
  consentPatientCount,
  registeredClientCount,
  registeredClientsReady = true,
  onAudienceChange,
}: EcommercePatientQuickStatsProps) {
  const theme = useTheme();

  const values: Record<PatientOpFilter, number> = {
    docs_pending: docsPendingCount,
    docs_rejected: docsRejectedCount,
    docs_to_approve: docsToApproveCount,
    awaiting_release: awaitingReleaseCount,
    docs_released: docsReleasedCount,
    purchase_completed: purchaseCompletedCount,
    all: totalCount,
  };

  const colors: Record<PatientOpFilter, string> = {
    docs_pending: theme.palette.warning.main,
    docs_rejected: theme.palette.error.main,
    docs_to_approve: theme.palette.secondary.main,
    awaiting_release: theme.palette.info.main,
    docs_released: theme.palette.success.main,
    purchase_completed: theme.palette.success.dark,
    all: theme.palette.primary.main,
  };

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      alignItems="stretch"
    >
      {PATIENT_OP_FILTER_GROUPS.map((group, groupIndex) => (
        <Fragment key={group.id}>
          {groupIndex > 0 ? (
            <Box
              aria-hidden
              sx={{
                display: { xs: "none", md: "block" },
                width: 3,
                alignSelf: "stretch",
                my: 0.75,
                borderRadius: 1,
                bgcolor: "text.disabled",
                flexShrink: 0,
              }}
            />
          ) : null}
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              p: 1,
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: 0,
              bgcolor: "action.hover",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              color="text.primary"
              fontWeight={700}
              sx={{
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontSize: "0.68rem",
                px: 0.25,
              }}
            >
              {group.title}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: 1 }}
            >
              {group.filters.map((filterId) => {
                const option = PATIENT_OP_FILTER_OPTIONS.find(
                  (item) => item.id === filterId,
                );
                const disabled = isDocDependentFilter(filterId) && !statsReady;
                return (
                  <EcommerceQuickStatCard
                    key={filterId}
                    label={option?.label ?? filterId}
                    value={values[filterId]}
                    selected={activeFilter === filterId}
                    disabled={disabled}
                    color={colors[filterId]}
                    icon={ICONS[filterId]}
                    tooltip={
                      disabled
                        ? "Carregando status de documentos e liberações…"
                        : option?.description ?? option?.label ?? filterId
                    }
                    onClick={() => onFilterChange(filterId)}
                  />
                );
              })}
            </Stack>
          </Paper>
        </Fragment>
      ))}
      <Box
        aria-hidden
        sx={{
          display: { xs: "none", md: "block" },
          width: 3,
          alignSelf: "stretch",
          my: 0.75,
          borderRadius: 1,
          bgcolor: "text.disabled",
          flexShrink: 0,
        }}
      />
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          p: 1,
          flex: { xs: "1 1 100%", sm: "1 1 auto" },
          minWidth: 0,
          bgcolor: "action.hover",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          color="text.primary"
          fontWeight={700}
          sx={{
            letterSpacing: 0.4,
            textTransform: "uppercase",
            fontSize: "0.68rem",
            px: 0.25,
          }}
        >
          Listas
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
          <EcommerceQuickStatCard
            label="Pacientes com consentimento"
            value={consentPatientCount}
            selected={audience === "consent_patients"}
            color={theme.palette.info.dark}
            icon={<HowToRegOutlined sx={{ fontSize: 20 }} />}
            tooltip="Quem autorizou a loja a ver documentos e pedidos."
            onClick={() => onAudienceChange("consent_patients")}
          />
          <EcommerceQuickStatCard
            label="Clientes cadastrados"
            value={registeredClientCount}
            selected={audience === "registered_clients"}
            selectedAlpha={0.15}
            valueLoading={!registeredClientsReady}
            color={theme.palette.secondary.dark}
            icon={<PersonAddAltOutlined sx={{ fontSize: 20 }} />}
            tooltip="Quem foi cadastrado nesta loja."
            onClick={() => onAudienceChange("registered_clients")}
          />
        </Stack>
      </Paper>
    </Stack>
  );
});
